import type { QueryFieldDescriptor, QueryFieldGroupDescriptor, QueryOperatorId, QueryValueKind } from '@aeppic/query/contracts'

const TEXT_OPERATORS: readonly QueryOperatorId[] = ['contains', 'not_contains', 'equals', 'not_equals', 'starts_with', 'is_one_of', 'is_not_one_of', 'empty', 'not_empty']
const NUMBER_OPERATORS: readonly QueryOperatorId[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'not_empty']
const DATETIME_OPERATORS: readonly QueryOperatorId[] = ['equals', 'not_equals', 'after', 'before', 'between', 'empty', 'not_empty']
const BOOLEAN_OPERATORS: readonly QueryOperatorId[] = ['is', 'is_not']
const REFERENCE_OPERATORS: readonly QueryOperatorId[] = ['is', 'is_not', 'is_one_of', 'is_not_one_of', 'empty', 'not_empty']
const DOCUMENT_METADATA_GROUP: QueryFieldGroupDescriptor = { id: 'document-metadata', label: 'Document metadata' }

function metadataField(path: string, label: string, valueKind: QueryValueKind, { required = false, multiple = false, editor }: { required?: boolean, multiple?: boolean, editor?: QueryFieldDescriptor['editor'] } = {}): QueryFieldDescriptor {
  const descriptor: QueryFieldDescriptor = {
    path,
    label,
    group: DOCUMENT_METADATA_GROUP,
    valueKinds: [valueKind],
    ...(editor ? { editor } : {}),
    cardinality: { min: multiple ? 0 : (required ? 1 : 0), max: multiple ? null : 1 },
    operators: operatorsForValueKind(valueKind),
    required,
    metadata: true,
  }

  if (valueKind === 'boolean') {
    descriptor.missingValue = false
  }

  return descriptor
}

export function operatorsForValueKind(valueKind: QueryValueKind): readonly QueryOperatorId[] {
  switch (valueKind) {
    case 'text':
      return TEXT_OPERATORS
    case 'number':
      return NUMBER_OPERATORS
    case 'datetime':
      return DATETIME_OPERATORS
    case 'boolean':
      return BOOLEAN_OPERATORS
    case 'select':
    case 'enum':
    case 'reference':
      return REFERENCE_OPERATORS
    default:
      return []
  }
}

export const DOCUMENT_METADATA_FIELDS: readonly QueryFieldDescriptor[] = [
  metadataField('id', 'ID', 'text', { required: true }),
  metadataField('p', 'Parent ID', 'text', { required: true }),
  metadataField('v', 'Version', 'text', { required: true }),
  metadataField('f.id', 'Form ID', 'text', { required: true }),
  metadataField('f.v', 'Form version', 'text', { required: true }),
  metadataField('hidden', 'Hidden', 'boolean'),
  metadataField('attached', 'Attached', 'boolean'),
  metadataField('readonly', 'Read only', 'boolean'),
  metadataField('language', 'Language', 'text'),
  metadataField('cloneOf.id', 'Clone source ID', 'reference'),
  metadataField('originalCloneOf.id', 'Original clone source ID', 'reference'),
  metadataField('locks.id', 'Lock ID', 'reference', { multiple: true }),
  metadataField('created.at', 'Created at', 'datetime', { required: true, editor: { kind: 'datetime' } }),
  metadataField('created.by', 'Created by', 'text', { required: true }),
  metadataField('created.from', 'Created from', 'text', { required: true }),
  metadataField('modified.at', 'Modified at', 'datetime', { required: true, editor: { kind: 'datetime' } }),
  metadataField('modified.by', 'Modified by', 'text', { required: true }),
  metadataField('modified.from', 'Modified from', 'text', { required: true }),
  metadataField('t', 'Event timestamp', 'number'),
  metadataField('ct', 'Creation event timestamp', 'number'),
  metadataField('valid', 'Form data valid', 'boolean'),
  metadataField('dts', 'Descendant timestamp', 'number'),
  metadataField('a_depth', 'Ancestry depth', 'number', { required: true }),
  metadataField('a', 'Ancestor IDs', 'text', { multiple: true }),
  metadataField('a_forms', 'Ancestor form IDs', 'text', { multiple: true }),
]
